'use strict';
/* ==========================================================================
   levels/pov.js — step "pov" (orientation any): WHOSE HEAD? (DESIGN.md §5.3, look §2).

   A case file (index card) pinned to the corkboard with a short passage typed on it, and a question sheet taped beside
   it. An attempt = 8 passages from content/bank.pov.json, drawn by the model (Narration.attempt('pov', json): seeded,
   every draw.require tag covered (all four points of view + a shift), easiest first, the right answers spread over the
   slots). Nothing here decides a point of view or an answer: core-local/narration.js computes them from the tags.

   Per passage:  the passage types on, sentence by sentence (numbered: the shift items tap them)
             →  name: "1 · WHICH POINT OF VIEW?" (shift items: "…does it START in?") 3 options with Florida's labels.
                A wrong one is crossed out and Ms. Park's red pen writes on the card why not (the model's why) + that
                label's one-line meaning (Narration.POVS[id].short). Pick again.
                Right: the eyes on the card open over the heads the narrator enters (pov().heads; the POV colour iris)
             →  ask: "2 · …": a choice (what the narrator can't know / whose heads / who is telling / perspective /
                why this point of view) or a tap (shift: "Tap the sentence where the point of view shifts": the card's
                sentences are the targets). Wrong = crossed out + the why on the card; pick again.
                The eyes of a "can't know" or "whose heads" item stay shut until this part is answered (open, they
                would give the answer away); a shift item's new head opens when the shift is found.
             →  note: the item's explain + the stars on the card, "Next passage".
   Stars: right name first try 1 + right ask first try 1; of 16, pass = bank.pov.json passStars (11).
   Game.progress.scores.pov = {best, last, of: 16}; first answers under Game.progress.answers['pov/<id>/name'] and
   ['pov/<id>/ask']. A pass → "Collect your stamp" (Game.complete('pov')); leaving a passed attempt without collecting
   still keeps the stamp. The end sheet lists the 8 passages with their points of view and stars.
   Attempt + seed: Game.progress.bank.pov = {pool, seed, attempt, ids, prev, open, run: {i, ev, res}}: a reload
   mid-attempt resumes the same passage with the same picks (?seed= / window.__bankSeed force a seed).

   Portrait phone (Dock.active): the card (title, the eyes, the named point of view) is the picture on the stage; the
   passage, the options, the notes and the buttons are dock widgets (a shift item's sentences are the dock options).
   Keys: 1–3 = option (1–n = sentence on a shift item), Enter / Space = start / next passage / end sheet's first button.

   QA hooks: sc.phase ('intro' | 'name' | 'ask' | 'note' | 'end'), sc.line (0–7), sc.item (the drawn instance),
   sc.items, sc.seed, sc.attempt, sc.resumed, sc.results, sc.stars, sc.picks, sc.apicks, sc.solution / sc.answer()
   ({name, ask}: the right option index / sentence index), sc.choose(i), sc.next(), sc.begin(fresh), sc.collect(),
   sc.done(), sc.eyes() (the eye states now), sc.notes() (the card's notes now); Levels.pov.solution,
   Levels.pov.targets() (the tap rects now: option rows or sentences, logical px), Levels.pov.preview(id) (that pool
   item as the current passage), Levels.pov.layout. Integration compat: sc.preview ({items}), sc.finishStub(stars).
   ========================================================================== */
(function () {
  const STEP = 'pov', TITLE = 'WHOSE HEAD?', OF_ITEM = 2, NEXT_GUARD = 0.3, CPS = 55;
  const Z = () => window.Zine;
  const json = () => Game.content.bank_pov || { items: [], draw: {} };
  const HAND = "'Caveat Brush'", BLUE = '#2b4a8c';
  const CARD = { x: 568, y: 594, w: 1016, h: 908 };          // the case file (desktop / landscape)
  const PANEL = { x: 1488, y: 594, w: 768, h: 908 };          // the question sheet
  const CARDD = { x: 960, y: 548, w: 1800, h: 1010 };         // portrait: the stage is only the card
  const DEFER = ['cant-know', 'heads'];                        // these items' eyes open only after Part B

  /* the source in small print (public-domain excerpts): "— Author, "Work", year (edition)" */
  function sourceText(src) {
    if (!src || typeof src !== 'object') return '';
    return `— ${src.author}, "${src.work}", ${src.edition ? src.edition : src.year}`;
  }
  const povColour = id => Narration.colour(id);
  const tellerName = (it, id) => { const n = Narration.castName(it, id); return id === (it.item && it.item.teller) ? (n === 'the narrator' ? 'the I' : `${n} (the I)`) : n; };

  /* the eyes on the card: every cast member (and "you" for second person); open over the heads the narrator enters */
  function eyeList(it) {
    const ids = [];
    const add = id => { if (id && !ids.includes(id)) ids.push(id); };
    it.pov.heads.forEach(add);
    if (it.shift) it.shift.to.heads.forEach(add);
    Object.keys(it.cast || {}).forEach(add);
    return ids.map(id => ({ id, name: tellerName(it, id) }));
  }

  /* the layout of one passage (desktop / landscape, or the portrait stage). Cached per line + UI scale + fonts. */
  function layout(it, docked) {
    const Zn = Z(), eyes = eyeList(it);
    if (docked) {
      const C0 = CARDD, top = C0.y - C0.h / 2;
      const ts = 84, er = Math.min(120, Math.floor((C0.w - 200) / Math.max(1, eyes.length) / 2.6));
      return { docked, C0, top, eyes, titleY: top + 170, ts, eyeR: er, eyeY: top + 400, eyeLs: 54, chipY: top + 720, chipS: 64 };
    }
    const C0 = CARD, top = C0.y - C0.h / 2, bottom = C0.y + C0.h / 2, left = C0.x - C0.w / 2;
    const rule = top + 70, TX = left + 100, TW = C0.w - 100 - 50, BX = left + 56;
    const eyeR = 40, eyeLs = Math.max(24, Math.round(UI.text(22)));
    const eyeY = rule + 26 + eyeR * 0.7, eyesBottom = eyeY + eyeR * 0.6 + eyeLs * 1.3;
    const eyeStep = Math.min(260, TW / Math.max(1, eyes.length));
    const noteS = Math.round(UI.text(30)), noteLh = Math.round(noteS * 1.12), noteMin = it.ask.type === 'tap' ? 0 : 3 * noteLh + 16;
    const tapH = it.ask.type === 'tap' ? UI.tap(84) : 0;
    const src = sourceText(it.source);
    let L;
    for (let size = 50; size >= 24; size -= 2) {
      const lh = Math.round(size * 1.3);
      let y = eyesBottom + 22;
      const blocks = it.sentences.map((s, k) => {
        const lines = Zn.wrap(s.text, TW, size), th = lines.length * lh, h = Math.max(th + 10, tapH);
        const b = { k, lines, y, h, cy: y + h / 2, ty: y + (h - th) / 2 + lh / 2 }; y += h + 4; return b;
      });
      const srcS = Math.min(Math.round(UI.text(24)), size), srcL = src ? Zn.wrap(src, TW, srcS) : [];
      const srcTop = y + 4; y += srcL.length ? srcL.length * srcS * 1.3 + 8 : 0;
      const notesTop = y + 14;
      L = { docked, C0, top, bottom, left, rule, TX, TW, BX, eyes, eyeR, eyeLs, eyeY, eyeStep, size, lh, blocks, srcS, src: srcL, srcTop, notesTop, noteS, noteLh };
      if (notesTop + noteMin <= bottom - 10) break;
    }
    // the question sheet: the UI sizes, shrunk a little only when a phone's big type would overflow it
    let PL;
    for (const k of [1, 0.94, 0.88, 0.82, 0.76, 0.7]) { PL = panelLayout(it, k); if (PL.askEnd <= PL.pbottom && PL.nameEnd <= PL.pbottom) break; }
    Object.assign(L, PL);
    return L;
  }
  /* the question sheet: the name phase (prompt + 3 rows) and the ask phase (the named chip, prompt, rows or a tap hint) */
  function panelLayout(it, k = 1) {
    const Zn = Z(), P0 = PANEL, top = P0.y - P0.h / 2, bottom = P0.y + P0.h / 2 - 16;
    const PX = P0.x - P0.w / 2 + 38, PW = P0.w - 76;
    const qs = Math.round(UI.text(30) * k), os = Math.round(UI.text(28) * k), hs = Math.round(UI.text(24) * k);
    const rowsAt = (labels, y) => labels.map((lab, i) => {
      const tl = UI._wrap(lab, PW - 96, `${os}px ${Zn.FONT}`), h = Math.max(UI.tap(84), Math.round(tl.length * os * 1.2 + 20));
      const r = { i, lines: tl, x: PX + PW / 2, y: y + h / 2, w: PW, h }; y += h + 12; return r;
    });
    // name phase
    const q1 = UI._wrap(`1 · ${it.name.prompt.toUpperCase()}`, PW, `${qs}px ${Zn.FONT}`);
    const q1Top = top + 64;
    const nameRows = rowsAt(it.name.options.map(o => o.label), q1Top + q1.length * qs * 1.25 + 18);
    // ask phase: "1 · POINT OF VIEW" + the chip, then "2 · prompt"
    const chipText = Narration.POVS[it.pov.id].label.toUpperCase();
    const cs = Math.max(20, Math.min(Math.round(UI.text(30) * k), Math.floor(PW / (Zn.measure(chipText, 1) + 2.8))));
    const hY = top + 56, chipY = hY + hs * 0.6 + cs * 0.75 + 16;
    const q2Top = chipY + cs * 0.75 + 34;
    const q2 = UI._wrap(`2 · ${it.ask.prompt || Narration.PROMPTS.shift}`, PW, `${qs}px ${Zn.FONT}`);
    const aTop = q2Top + q2.length * qs * 1.25 + 18;
    const askRows = it.ask.type === 'tap' ? [] : rowsAt(it.ask.options.map(o => o.label), aTop);
    const hint = it.ask.type === 'tap' ? UI._wrap('Tap it on the case file.', PW, `${os}px ${Zn.FONT}`) : [];
    const rowsEnd = askRows.length ? askRows[askRows.length - 1].y + askRows[askRows.length - 1].h / 2 : aTop + hint.length * os * 1.3;
    const btnH = UI.tap(96), starsS = Math.round(UI.text(40));
    // the note: every row kept when the stars + the button still fit under them, else only the right row
    const full = rowsEnd + 24 + starsS * 1.3 + 20 + btnH <= bottom;
    const compactRow = askRows.length ? rowsAt([askRows[it.ask.answerIndex].lines.join(' ')], aTop)[0] : null;
    // a shift item's note: "IT SHIFTS TO" + the new point of view's chip
    const toText = it.shift ? Narration.POVS[it.shift.to.id].label.toUpperCase() : '';
    const toS = it.shift ? Math.max(20, Math.min(cs, Math.floor(PW / (Zn.measure(toText, 1) + 2.8)))) : 0;
    const toHY = (it.ask.type === 'tap' ? q2Top : aTop) + hs * 0.6, toY = toHY + hs * 0.6 + toS * 0.75 + 16;
    const noteEnd = it.shift ? toY + toS * 0.75 : full ? rowsEnd : compactRow ? compactRow.y + compactRow.h / 2 : rowsEnd;
    const tap = it.ask.type === 'tap';
    const btnY = tap ? bottom - btnH / 2 - 10 : Math.min(bottom - btnH / 2, Math.max(noteEnd + 24 + starsS * 1.3 + 20 + btnH / 2, bottom - btnH / 2 - 40));
    const starsY = tap ? btnY - btnH / 2 - 24 - starsS * 0.65 : noteEnd + 24 + starsS * 0.65;
    // a shift item's notes on the sheet: under the tap hint (ask) / under the new chip (note)
    const pNotesAsk = aTop + hint.length * os * 1.3 + 20, pNotesNote = tap && it.shift ? toY + toS * 0.75 + 24 : noteEnd + 24;
    return { pNotesAsk, pNotesNote, toText, toS, toHY, toY, P0, ptop: top, pbottom: bottom, PX, PW, qs, os, hs, q1, q1Top, nameRows, chipText, cs, hY, chipY, q2, q2Top, aTop, askRows, hint, full, compactRow, starsS, starsY, btnH, btnY,
      nameEnd: nameRows[nameRows.length - 1].y + nameRows[nameRows.length - 1].h / 2, askEnd: rowsEnd };
  }

  /* a five-point star (the fonts have no star glyph) */
  function star(x, y, r, color) {
    g.save(); g.fillStyle = color; g.beginPath();
    for (let k = 0; k < 10; k++) { const a = -Math.PI / 2 + k * Math.PI / 5, rr = k % 2 ? r * 0.45 : r; g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
    g.closePath(); g.fill(); g.restore();
  }
  function inkTick(x, y, s, color, t0) {
    const pr = clamp((T - t0) / 0.3);
    if (pr <= 0) return;
    ink([[x - 18 * s, y], [x - 4 * s, y + 16 * s], [x + 24 * s, y - 22 * s]], { w: 6, color, seed: Math.round(x + y) % 97 + 1, prog: pr, passes: 1, step: 6 });
  }

  class PovScene extends Scene {
    get n() { return this.items ? this.items.length : (json().draw && json().draw.n) || 8; }
    get of() { return this.n * OF_ITEM; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 2 / 3)); }
    get item() { return this.items ? this.items[this.line] : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() { const it = this.item; return it ? { name: it.name.answerIndex, ask: it.ask.type === 'tap' ? it.ask.answers[0] : it.ask.answerIndex } : null; }
    answer() { return this.solution; }
    get preview() {
      if (this.items) return { seed: this.seed, items: this.items };
      if (!this._peek) { const f = Bank.seedOverride(); this._peek = Narration.drawPov(json(), { seed: f != null ? f : 1 }); }
      return this._peek;
    }

    enter() {
      this.phase = 'intro'; this.items = null; this.line = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map(); this.quiet = false;
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }

    /* ---- the attempt (Narration.attempt: seeded, saved in Game.progress.bank.pov) ---- */
    begin(fresh = false) {
      this.bankStamp();
      const P_ = Game.progress, S = P_.bank || (P_.bank = {}), st = S[STEP];
      const run = !fresh && st && st.open && st.pool === STEP ? st.run : null;
      const a = Narration.attempt(STEP, json(), { fresh });
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.items = a.items;
      this.results = []; this.passed = false; this.collected = false; this.finalStars = null; this.lays.clear();
      if (a.resumed && run && this.restore(run)) return;
      this.startLine(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.items.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.lineAt = Game.time - 8; this.quiet = true;
        for (const v of run.ev || []) this.choose(v);
        // the replayed picks happened "before": nothing animates in again
        if (this.rightAt != null) { this.rightAt = Game.time - 6; this.askAt = Game.time - 5; }
        if (this.noteAt != null) this.noteAt = Game.time - 4;
        this.pickedAt = Game.time - 4;
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[pov] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.line = i; this.phase = 'name';
      this.ev = []; this.picks = []; this.apicks = []; this.lastWrong = null;
      this.lineAt = Game.time; this.rightAt = this.askAt = this.noteAt = this.pickedAt = null;
    }
    startLine(i) {
      this.setup(i);
      this.save();
      if (!this.quiet) { if (Audio.has('typewriter')) Audio.play('typewriter', { gain: 0.45 }); else Audio.play('paper', { gain: 0.4 }); }
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.line, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, alt, o) { if (this.quiet) return; const n = Audio.has(name) ? name : alt; if (n) Audio.play(n, o); }

    /* ---- pick an option (name phase / choice ask) or a sentence (tap ask) ---- */
    choose(i) {
      const it = this.item;
      if (!it) return false;
      if (this.phase === 'name') {
        const q = it.name;
        if (!(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
        this.ev.push(i);
        if (!this.picks.length) Game.progress.answer(`${STEP}/${it.id}/name`, q.options[i].id);   // first try only
        this.picks.push(i); this.pickedAt = Game.time;
        if (i === q.answerIndex) {
          this.rightAt = Game.time; this.askAt = Game.time + 0.7; this.phase = 'ask'; this.lastWrong = null;
          this.sfx('ding', null, { gain: 0.6 });
        } else { this.lastWrong = i; this.sfx('thunk', null, { gain: 0.4, rate: 0.9 }); }
        this.save();
        return true;
      }
      if (this.phase === 'ask') {
        const a = it.ask, tap = a.type === 'tap', n = tap ? it.sentences.length : a.options.length;
        if (!(i >= 0 && i < n) || this.apicks.includes(i)) return false;
        this.ev.push(i);
        if (!this.apicks.length) Game.progress.answer(`${STEP}/${it.id}/ask`, tap ? i : a.options[i].label);
        this.apicks.push(i); this.pickedAt = Game.time;
        const right = tap ? a.answers.includes(i) : i === a.answerIndex;
        if (right) {
          const ns = this.picks.length === 1 ? 1 : 0, as = this.apicks.length === 1 ? 1 : 0;
          this.results[this.line] = { line: this.line, id: it.id, pov: it.pov.id, shift: !!it.shift, wrongNames: this.picks.length - 1, wrongAsks: this.apicks.length - 1, nameStar: ns, askStar: as, stars: ns + as };
          this.phase = 'note'; this.noteAt = Game.time; this.lastWrong = null;
          this.sfx(ns + as === 2 ? 'sparkle' : 'ding', 'ding', { gain: 0.5 });
        } else { this.lastWrong = i; this.sfx('thunk', null, { gain: 0.35, rate: 0.8 }); }
        this.save();
        return true;
      }
      return false;
    }
    next() {
      if (this.phase !== 'note' || Game.time - this.noteAt < NEXT_GUARD) return false;
      Audio.play('paper', { gain: 0.5 });
      if (this.line < this.items.length - 1) this.startLine(this.line + 1);
      else this.finish();
      return true;
    }
    finish(stars = this.stars) {
      this.phase = 'end'; this.endAt = Game.time; this.passed = stars >= this.passStars; this.finalStars = stars;
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S) delete S.run;
      Narration.finish(STEP);                                                     // the next attempt draws new passages
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, stars), last: stars, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.items) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    /* integration compat: record `stars` (default all 16) and complete the step */
    finishStub(stars = this.of) {
      if (this.collected) return;
      if (!this.items) this.begin();
      if (this.phase !== 'end') this.finish(stars);
      this.collected = true; Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new passages)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new passages)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => Game.replace(new Levels.map()) }];
    }

    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'name' || this.phase === 'ask') { if (n >= 1 && n <= 9) this.choose(n - 1); }
      else if (this.phase === 'note') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---- the eyes: when each opens (null = stays shut) ---- */
    eyeAt(id) {
      const it = this.item, defer = DEFER.includes(it.ask.kind);
      const k = it.pov.heads.indexOf(id);
      if (k >= 0) { const at = defer ? this.noteAt : this.rightAt; return at == null ? null : at + 0.35 + k * 0.25; }
      if (it.shift && it.shift.to.heads.includes(id)) return this.noteAt == null ? null : this.noteAt + 0.35 + it.shift.to.heads.indexOf(id) * 0.25;
      return null;
    }
    eyes() { const it = this.item; if (!it) return []; return eyeList(it).map(e => { const at = this.eyeAt(e.id); return { id: e.id, name: e.name, open: at != null && T >= at }; }); }

    /* ---- the card's notes (Ms. Park's pen): why a pick is wrong; the explain and the stars at the end ---- */
    notes() {
      const it = this.item, out = [];
      if (!it) return out;
      if (this.phase === 'name' && this.lastWrong != null) {
        const o = it.name.options[this.lastWrong], D = Narration.POVS[o.id];
        out.push(['red', `Not ${D.label.toLowerCase()}. ${it.name.why[this.lastWrong] || ''}`.trim(), this.pickedAt]);
        out.push(['grey', `${D.label}: ${D.short}.`, this.pickedAt + 0.3]);
      } else if (this.phase === 'ask' && this.lastWrong != null) {
        const a = it.ask;
        const why = a.type === 'tap' ? a.whyTap(this.lastWrong) : a.why[this.lastWrong];
        out.push(['red', why || 'Not that one. Look again at whose head the narrator is in.', this.pickedAt]);
      } else if (this.phase === 'note') {
        out.push(['blue', it.explain, this.noteAt + 0.2]);
      }
      return out;
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const key = this.line + '|' + this.item.id + '|' + (docked ? 'd' : 'w') + '|' + Math.round(UI.text(30)) + '|' + Math.round(UI.tap(84)) + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { if (this.lays.size > 12) this.lays.clear(); L = layout(this.item, docked); this.lays.set(key, L); }
      return L;
    }
    draw() {
      const docked = UI.docked(), Zn = Z();
      Zn.cork();
      UI.hud(TITLE, '');
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked);
      if (docked) { this.drawStage(L); return this.drawDock(); }
      this.drawCard(L);
      this.drawPanel(L);
    }

    /* the eyes row (shared by the card and the portrait stage) */
    drawEyes(L, x0, w, y, r, ls) {
      const Zn = Z(), it = this.item, n = L.eyes.length, step = Math.min(r * 6.5, w / Math.max(1, n));
      const colour = povColour(it.pov.id), colour2 = it.shift ? povColour(it.shift.to.id) : colour;
      L.eyes.forEach((e, k) => {
        const x = x0 + w / 2 + (k - (n - 1) / 2) * step, at = this.eyeAt(e.id);
        const open = at == null ? 0 : E.outBack(clamp((T - at) / 0.45), 1.6);
        const later = it.shift && !it.pov.heads.includes(e.id);
        Zn.eye(x, y, r, { open, fill: later ? colour2 : colour, seed: 300 + k * 7 + this.line, color: open > 0.05 ? Zn.C.ink : Zn.C.grey });
        Zn.type(e.name, x, y + r * 0.62 + ls * 0.7, ls, { align: 'center', seed: 320 + k, color: open > 0.05 ? Zn.C.ink : Zn.C.grey });
      });
    }

    /* the case file: header, the eyes, the numbered sentences (tap targets on a shift item), the notes */
    drawCard(L) {
      const Zn = Z(), it = this.item, t0 = this.lineAt, C0 = L.C0;
      const k = E.outCubic(clamp((T - t0) / 0.35)), slide = (1 - k) * 60;
      g.save(); g.translate(slide, 0);
      Zn.card(C0.x, C0.y, C0.w, C0.h, { seed: 211 + this.line * 7, rot: 0, title: it.title.toUpperCase(), lines: false, t0: t0 + 0.1 });
      // header right: "3 / 8" and the stars so far
      const hy = L.top + 38, hs = Math.round(UI.text(26));
      const sw = Zn.type(String(this.stars), C0.x + C0.w / 2 - 30, hy, hs, { align: 'right', seed: 6, color: Zn.C.grey });
      star(C0.x + C0.w / 2 - 50 - sw, hy, hs * 0.45, Zn.C.red);
      Zn.type(`${this.line + 1} / ${this.n}`, C0.x + C0.w / 2 - 90 - sw, hy, hs, { align: 'right', seed: 5, color: Zn.C.grey });
      this.drawEyes(L, L.TX, L.TW, L.eyeY, L.eyeR, L.eyeLs);
      // the sentences
      const tapping = this.phase === 'ask' && it.ask.type === 'tap' && T >= this.askAt, a = it.ask;
      const rightTap = a.type === 'tap' && this.phase === 'note' ? a.answers[0] : -1;
      let before = 0;
      const typeAt = t0 + 0.4;
      L.blocks.forEach(b => {
        const crossed = a.type === 'tap' && this.apicks.includes(b.k) && b.k !== rightTap && this.phase !== 'name';
        const hit = tapping && !crossed ? UI._hit('pv-s' + this.line + '_' + b.k, L.left + L.C0.w / 2 - 20, b.cy, L.C0.w - 60, b.h - 2) : { hover: false, clicked: false };
        if (hit.hover) { g.save(); g.fillStyle = 'rgba(255,240,74,0.35)'; g.fillRect(L.left + 24, b.y + 2, L.C0.w - 70, b.h - 6); g.restore(); }
        if (b.k === rightTap) b.lines.forEach((l, j) => Zn.highlight(L.TX - 6, b.ty + j * L.lh, Zn.measure(l, L.size) + 12, L.size * 1.1, povColour(it.shift ? it.shift.to.id : it.pov.id), { t0: this.noteAt + j * 0.12, seed: 40 + j }));
        // the number badge (once its sentence starts typing)
        if (T >= typeAt + before / CPS) {
        g.save(); g.fillStyle = crossed ? Zn.C.grey : tapping ? Zn.C.red : Zn.C.ink; g.beginPath(); g.arc(L.BX, b.ty, 20, 0, TAU); g.fill();
        g.font = `24px ${Zn.FONT}`; g.fillStyle = Zn.C.white; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(b.k + 1), L.BX, b.ty + 1); g.restore();
        }
        b.lines.forEach((l, j) => {
          Zn.type(l, L.TX, b.ty + j * L.lh, L.size, { t0: typeAt + before / CPS, cps: CPS, seed: 10 + b.k * 5 + j + this.line * 13, color: crossed ? Zn.C.grey : Zn.C.ink, cursor: b.k === L.blocks.length - 1 && j === b.lines.length - 1 });
          before += l.length + 1;
        });
        if (crossed) {
          const w0 = Zn.measure(b.lines[0], L.size), at = b.k === this.lastWrong ? this.pickedAt : -1e9;
          ink([[L.TX - 8, b.ty + 8], [L.TX + w0 + 8, b.ty - 8]], { w: 5, color: Zn.C.red, seed: 80 + b.k, prog: clamp((T - at) / 0.3), passes: 1 });
        }
        if (b.k === rightTap) inkTick(L.left + L.C0.w - 60, b.ty, 1, Zn.C.red, this.noteAt + 0.3);
        if (hit.clicked) this.choose(b.k);
      });
      const srcAt = typeAt + before / CPS;
      L.src.forEach((s, j) => Zn.type(s, L.TX, L.srcTop + (j + 0.5) * L.srcS * 1.3, L.srcS, { t0: srcAt + j * 0.2, cps: 90, color: Zn.C.grey, seed: 30 + j }));
      // Ms. Park's notes (a shift item's go on the question sheet: its sentences need the card's room)
      if (a.type !== 'tap') this.drawNotes(L.TX, L.notesTop, L.TW, L.noteS, L.noteLh);
      g.restore();
    }
    drawNotes(x, y, w, size, lh) {
      const Zn = Z(), col = { red: Zn.C.red, grey: '#55524c', blue: BLUE };
      this.notes().forEach(([kind, text, at], nn) => {
        const lines = UI._wrap(text, w, `${size}px ${HAND}`);
        lines.forEach((l, j) => hand(l, x, y + (j + 0.5) * lh, size, at + j * 0.2, { align: 'left', color: col[kind], seed: 200 + nn * 7 + j, rot: -0.01, dur: 0.7 }));
        y += lines.length * lh + 6;
      });
    }

    /* the question sheet */
    drawPanel(L) {
      const Zn = Z(), it = this.item, P0 = L.P0;
      Zn.page(P0.x, P0.y, P0.w, P0.h, { seed: 511 + (this.line % 3), rot: 0.004, tape: 1, dark: 0.35 });
      if (this.phase === 'name') {
        const t0 = this.lineAt + 0.5;
        L.q1.forEach((l, j) => Zn.type(l, L.PX, L.q1Top + (j + 0.5) * L.qs * 1.25, L.qs, { t0: t0 + j * 0.3, cps: 50, seed: 50 + j, color: BLUE }));
        this.drawRows(L, L.nameRows, 'name', t0 + 0.3, this.picks, it.name.answerIndex, id => povColour(it.name.options[id].id));
        return;
      }
      // the named point of view
      Zn.type('1 · POINT OF VIEW', L.PX, L.hY, L.hs, { seed: 52, color: Zn.C.grey });
      const cw = Zn.measure(L.chipText, L.cs) + L.cs * 2.6;
      Zn.label(it.pov.id, L.PX + cw / 2, L.chipY, { size: L.cs, rot: -0.012, s: pop(T, this.rightAt, 0.3), seed: 90 });
      if (T < this.askAt) return;
      const note = this.phase === 'note', a = it.ask;
      if (!(note && a.type === 'tap')) L.q2.forEach((l, j) => Zn.type(l, L.PX, L.q2Top + (j + 0.5) * L.qs * 1.25, L.qs, { t0: this.askAt + j * 0.3, cps: 50, seed: 60 + j, color: BLUE }));
      if (a.type === 'tap') {
        if (!note) L.hint.forEach((l, j) => Zn.type(l, L.PX, L.aTop + (j + 0.5) * L.os * 1.3, L.os, { t0: this.askAt + 0.5, cps: 60, seed: 70 + j, color: Zn.C.red }));
        else if (it.shift) {
          Zn.type('IT SHIFTS TO', L.PX, L.toHY, L.hs, { t0: this.noteAt, cps: 40, seed: 72, color: Zn.C.grey });
          const tw = Zn.measure(L.toText, L.toS) + L.toS * 2.6;
          Zn.label(it.shift.to.id, L.PX + tw / 2, L.toY, { size: L.toS, rot: 0.012, s: pop(T, this.noteAt + 0.2, 0.3), seed: 94 });
        }
      } else if (note && !L.full) {
        this.drawRows(L, [Object.assign({}, L.compactRow, { i: a.answerIndex })], 'ask', -1e6, this.apicks, a.answerIndex, () => povColour(it.pov.id));
      } else this.drawRows(L, L.askRows, 'ask', this.askAt + 0.3, this.apicks, a.answerIndex, () => povColour(it.pov.id));
      if (a.type === 'tap') this.drawNotes(L.PX, note ? L.pNotesNote : L.pNotesAsk, L.PW, L.noteS, L.noteLh);
      if (note) this.drawNoteFoot(L);
    }
    drawRows(L, rows, kind, t0, picks, right, colourOf) {
      const Zn = Z(), note = this.phase === 'note', live = (kind === 'name' && this.phase === 'name') || (kind === 'ask' && this.phase === 'ask');
      rows.forEach(r => {
        const s = pop(T, t0 + r.i * 0.08, 0.3, 1.6), crossed = picks.includes(r.i) && r.i !== right, isRight = note && r.i === right;
        if (s <= 0) return;
        const hit = !live || crossed ? { hover: false, clicked: false } : UI._hit('pv-' + kind + this.line + '_' + r.i, r.x, r.y, r.w, r.h - 4);
        g.save(); g.translate(r.x, r.y); g.scale(s, s);
        paperRect(0, 0, Math.round(r.w / 10) * 10, Math.round((r.h - 8) / 2) * 2, hit.hover ? '#fffbe0' : crossed ? '#e4e1d8' : note && !isRight ? '#efede6' : Zn.C.white, { seed: 120 + r.i, depth: hit.hover ? 1.2 : 0.6, boil: false });
        g.restore();
        if (isRight) Zn.highlight(r.x - r.w / 2 + 76, r.y, r.w - 130, r.h - 26, colourOf(r.i), { t0: this.noteAt, seed: 130 + r.i, alpha: 0.5 });
        g.save(); g.fillStyle = crossed ? Zn.C.grey : Zn.C.ink; g.fillRect(r.x - r.w / 2 + 20, r.y - 22, 44, 44);
        g.font = `28px ${Zn.FONT}`; g.fillStyle = Zn.C.white; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(r.i + 1), r.x - r.w / 2 + 42, r.y + 2); g.restore();
        const ty = r.y - (r.lines.length - 1) * L.os * 1.2 / 2;
        r.lines.forEach((l, j) => Zn.type(l, r.x - r.w / 2 + 84, ty + j * L.os * 1.2, L.os, { seed: 140 + r.i * 5 + j, color: crossed || (note && !isRight) ? Zn.C.grey : Zn.C.ink }));
        if (crossed) {
          const mw = Math.max(...r.lines.map(l => Zn.measure(l, L.os))), a = r.i === this.lastWrong && live ? this.pickedAt : -1e9;
          ink([[r.x - r.w / 2 + 76, r.y + 10], [r.x - r.w / 2 + 96 + mw, r.y - 10]], { w: 5, color: Zn.C.red, seed: 150 + r.i, prog: clamp((T - a) / 0.3), passes: 1 });
        }
        if (kind === 'name' && this.phase !== 'name' && r.i === right) inkTick(r.x + r.w / 2 - 44, r.y, 1, Zn.C.red, this.rightAt);
        if (isRight) inkTick(r.x + r.w / 2 - 44, r.y, 1, Zn.C.red, this.noteAt + 0.2);
        if (hit.clicked) this.choose(r.i);
      });
    }
    drawNoteFoot(L) {
      const Zn = Z(), r = this.results[this.line] || { stars: 0 }, at = this.noteAt;
      const msg = r.stars === 2 ? '+2  Sharp eyes!' : r.stars === 1 ? '+1  Close.' : '+0  Next one.';
      const sx = L.PX + L.starsS * 0.5;
      for (let k = 0; k < 2; k++) if (T >= at + 0.2 + k * 0.15) star(sx + k * L.starsS * 1.05, L.starsY, L.starsS * 0.45, k < r.stars ? Zn.C.red : '#c9c4b8');
      hand(msg, sx + L.starsS * 1.9, L.starsY, L.starsS, at + 0.3, { align: 'left', color: Zn.C.red, seed: 170 });
      hand('— Ms. P.', L.PX + L.PW, L.starsY + L.starsS * 0.1, Math.round(L.starsS * 0.8), at + 0.6, { align: 'right', color: BLUE, seed: 171 });
      if (Game.time - this.noteAt > NEXT_GUARD) {
        const label = this.line < this.n - 1 ? 'Next passage' : 'See my stars';
        if (UI.button('pv-next', label, L.PX + L.PW / 2, L.btnY, { w: L.PW, h: 96, color: Zn.C.red, size: 40 })) this.next();
      }
    }

    /* ---- portrait: the card is the picture; everything to read and tap is in the dock ---- */
    drawStage(L) {
      const Zn = Z(), it = this.item, C0 = L.C0, t0 = this.lineAt;
      Zn.card(C0.x, C0.y, C0.w, C0.h, { seed: 211 + this.line * 7, rot: -0.004, lines: false, title: `${this.line + 1} / ${this.n}`, t0 });
      const ttl = it.title.toUpperCase(), ts = Math.min(L.ts, Math.floor((C0.w - 160) / Math.max(1, Zn.measure(ttl, 1))));
      Zn.type(ttl, C0.x, L.titleY, ts, { t0: t0 + 0.2, cps: 40, align: 'center', seed: 5 });
      this.drawEyes(L, C0.x - C0.w / 2 + 60, C0.w - 120, L.eyeY, L.eyeR, L.eyeLs);
      if (this.rightAt != null) {
        const cs = Math.min(L.chipS, Math.floor((C0.w - 200) / (Zn.measure(Narration.POVS[it.pov.id].label.toUpperCase(), 1) + 2.8)));
        Zn.label(it.pov.id, C0.x, L.chipY, { size: cs, rot: -0.02, s: pop(T, this.rightAt, 0.3), seed: 60 });
      }
      if (this.phase === 'note') {
        const r = this.results[this.line] || { stars: 0 };
        for (let k = 0; k < 2; k++) if (T >= this.noteAt + 0.2 + k * 0.15) star(C0.x - 70 + k * 140, L.chipY + 170, 56, k < r.stars ? Zn.C.red : '#c9c4b8');
      }
    }
    drawDock() {
      const it = this.item, id = this.attempt + '_' + this.line, a = it.ask, tap = a.type === 'tap';
      const passage = it.sentences.map((s, k) => `${k + 1}. ${s.text}`).concat(sourceText(it.source) ? [sourceText(it.source)] : []);
      Dock.heading('pv-h' + id, `${this.line + 1} / ${this.n} · ${it.title}`);
      if (!(tap && this.phase !== 'name')) Dock.text('pv-pass' + id, passage, { cls: 'note' });
      const q = it.name;
      if (this.phase === 'name') {
        const st = q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        const notes = q.options.map((o, i) => { if (!this.picks.includes(i)) return ''; const D = Narration.POVS[o.id]; return [q.why[i], `${D.label}: ${D.short}.`].filter(Boolean).join(' '); });
        Dock.choices('pv-name' + id, { prompt: `1 · ${q.prompt}`, options: q.options.map(o => o.label), state: st, notes, onChoose: i => this.choose(i) });
        return;
      }
      Dock.choices('pv-name' + id, { prompt: `1 · ${q.prompt}`, options: q.options.map(o => o.label), state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      const note = this.phase === 'note';
      const right = tap ? a.answers[0] : a.answerIndex;
      const opts = tap ? it.sentences.map((s, k) => `${k + 1}. ${s.text}`) : a.options.map(o => o.label);
      const st = opts.map((o, i) => (note ? (i === right ? 'right' : this.apicks.includes(i) ? 'wrong' : 'faded') : this.apicks.includes(i) ? 'wrong' : ''));
      const notes = opts.map((o, i) => (!this.apicks.includes(i) || i === right ? '' : (tap ? a.whyTap(i) : a.why[i]) || 'Not that one.'));
      Dock.choices('pv-ask' + id, { prompt: `2 · ${a.prompt || Narration.PROMPTS.shift}`, options: opts, state: st, notes, locked: note, onChoose: i => this.choose(i) });
      if (note) {
        const r = this.results[this.line] || { stars: 0 };
        Dock.text('pv-park' + id, [it.explain, `Ms. Park: ${r.stars === 2 ? '+2 stars. Sharp eyes!' : r.stars === 1 ? '+1 star. Close.' : 'No stars on this one. Next one.'}`], { cls: 'note', reveal: true });
        Dock.button({ id: 'next', glyph: this.line < this.n - 1 ? 'Next passage' : 'See my stars', label: 'next', aria: 'next passage', go: true, onClick: () => this.next() });
      }
    }

    introLines() {
      return ['Eight short passages. Whose head is the narrator in?', '1. Which point of view? First-person limited, third-person limited, third-person omniscient (or second person).',
        '2. Then: what can this narrator know, and why tell it this way? Some passages shift: tap where.', `A star for each right first try: ${this.passStars} of ${this.of} to pass.`];
    }
    drawIntroEyes(x, y, r, gap, t0) {
      ['first-limited', 'third-limited', 'third-omniscient'].forEach((id, i) => Z().eye(x + (i - 1) * gap, y, r, { open: E.outBack(clamp((T - t0 - i * 0.3) / 0.45), 1.6), fill: povColour(id), seed: 700 + i * 5 }));
    }
    drawIntro(docked) {
      const Zn = Z(), t0 = this.enteredAt, lines = this.introLines();
      if (docked) {
        Zn.page(960, 548, 1500, 900, { seed: 311, rot: -0.01, tape: 1 });
        Zn.type(TITLE, 960, 380, 110, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 5 });
        Zn.highlight(560, 390, 800, 110, povColour('third-omniscient'), { t0: t0 + 0.7, seed: 7 });
        this.drawIntroEyes(960, 680, 110, 360, t0 + 0.9);
        Dock.heading('pv-intro', 'Whose Head?', { cls: 'big' });
        Dock.text('pv-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start', label: 'start', aria: 'Start naming points of view', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      const rise = E.outBack(P(T, t0 + 0.2, t0 + 0.7), 1.4);
      Puppet.draw('park', 1640, 1080 + (1 - rise) * 500, 760, { facing: -1, t: T, env: 0 });
      Zn.page(800, 590, 1260, 880, { seed: 311, rot: -0.008, tape: 1 });
      Zn.type(TITLE, 800, 250, 76, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 5 });
      Zn.highlight(800 - 280, 258, 560, 70, povColour('third-omniscient'), { t0: t0 + 0.7, seed: 7 });
      const fs = Math.round(UI.text(34));
      let y = 350;
      lines.forEach((l, i) => UI._wrap(l, 1040, `${fs}px ${Zn.FONT}`).forEach(tl => {
        Zn.type(tl, 800 - 520, y, fs, { t0: t0 + 0.5 + i * 0.35, cps: 80, seed: 20 + i, color: i === 3 ? Zn.C.red : Zn.C.ink });
        y += fs * 1.4;
      }));
      this.drawIntroEyes(800, y + 60, 50, 200, t0 + 1.6);
      const bh = UI.tap(110);
      if (this.t > 0.8 && UI.button('pv-start', 'Start', 800, Math.min(1080 - 50 - bh / 2, y + 150 + bh / 2), { w: 420, h: 110, color: Zn.C.red, size: 56 })) this.begin();
    }
    drawEnd(docked) {
      const Zn = Z(), s = this.finalStars != null ? this.finalStars : this.stars, of = this.of, btns = this.endButtons();
      const rows = this.items.map((it, i) => ({ it, r: this.results[i] || { stars: 0 } }));
      const povLine = it => (it.shift ? `${Narration.POVS[it.shift.from.id].label}, then shifts` : Narration.POVS[it.pov.id].label);
      if (docked) {
        Zn.page(960, 548, 1500, 900, { seed: 411, rot: -0.01, tape: 1 });
        Zn.type(`${s} / ${of}`, 960, 420, 150, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 950 });
        Zn.stamp(this.passed ? 'FACT-CHECKED' : 'TRY AGAIN', 960, 700, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.3 });
        Dock.heading('pv-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('pv-end', [this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars to pass. New passages?`]);
        Dock.list('pv-end', rows.map((x, i) => ({ badge: String(i + 1), color: povColour(x.it.pov.id), lines: [x.it.title, `${povLine(x.it)} · ${x.r.stars} of 2 stars`] })));
        Dock.text('pv-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('pv-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Zn.page(760, 590, 1380, 920, { seed: 411, rot: -0.006, tape: 1 });
      Zn.type('THE CASE BOARD', 190, 190, 44, { t0: this.endAt, cps: 40, seed: 900, color: BLUE });
      const fs = Math.round(UI.text(28)), top = 240, rh = Math.min(96, (1020 - top) / rows.length), X0 = 150, W = 1220;
      rows.forEach((x, i) => {
        const y = top + (i + 0.5) * rh, at = this.endAt + 0.3 + i * 0.1;
        Zn.mark(x.it.pov.id, X0 + 20, y, W - 320, rh * 0.62, { t0: at, seed: 910 + i, alpha: 0.55 });
        Zn.eye(X0 + 56, y, fs * 0.8, { open: E.outBack(clamp((T - at) / 0.4), 1.6), fill: povColour(x.it.pov.id), seed: 920 + i * 3 });
        let t = x.it.title;
        const maxW = W - 480;
        if (Zn.measure(t, fs) > maxW) { while (Zn.measure(t + '…', fs) > maxW && t.length > 8) t = t.slice(0, -1); t = t.trimEnd() + '…'; }
        Zn.type(t, X0 + 110, y - fs * 0.32, fs, { seed: 930 + i });
        Zn.type(povLine(x.it).toUpperCase(), X0 + 110, y + fs * 0.62, Math.round(fs * 0.72), { seed: 940 + i, color: Zn.C.grey });
        if (T >= at + 0.2) for (let k = 0; k < 2; k++) star(X0 + W - 150 + k * fs * 1.2, y, fs * 0.5, k < x.r.stars ? Zn.C.red : '#c9c4b8');
      });
      const RX = 1670, bh = UI.tap(96), b0 = 1080 - 30 - bh / 2 - (bh + 20);
      Zn.page(RX, 440, 440, 560, { seed: 419, rot: 0.012, tape: 1, dark: 0.35 });
      Zn.type(`${s} / ${of}`, RX, 270, 104, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 950 });
      Zn.stamp(this.passed ? 'FACT-CHECKED' : 'TRY AGAIN', RX, 440, { rot: -0.1, s: pop(T, this.endAt + 0.5, 0.3) * 0.55 });
      const msg = this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars to pass.`, ms = Math.round(UI.text(36));
      UI._wrap(msg, 380, `${ms}px ${HAND}`).forEach((l, j) => hand(l, RX, 580 + j * ms * 1.1, ms, this.endAt + 0.8, { color: Zn.C.red, seed: 980 + j, dur: 0.5 }));
      hand(`quiz code ${this.seed}`, RX, 690, 28, this.endAt + 0.6, { color: Zn.C.grey, seed: 985 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('pv-end' + i, b.label, RX, b0 + i * (bh + 20), { w: 440, h: 96, color: i === 0 ? Zn.C.red : BLUE, size: 34 })) b.go(); });
    }
  }

  PovScene.STEP = STEP;
  PovScene.layout = layout;
  PovScene.eyeList = eyeList;
  PovScene.sourceText = sourceText;
  Object.defineProperty(PovScene, 'solution', { get() { const s = Game.current(); return s instanceof PovScene ? s.solution : null; } });
  /* the live scene's tap rects now (logical px): option rows (name / choice ask) or the card's sentences (tap ask) */
  PovScene.targets = () => {
    const s = Game.current(); if (!(s instanceof PovScene) || !s.item || UI.docked()) return [];
    const L = s.lay(false);
    if (s.phase === 'name') return L.nameRows.map(r => ({ i: r.i, id: 'pv-name' + s.line + '_' + r.i, x: r.x, y: r.y, w: r.w, h: r.h }));
    if (s.phase === 'ask' && s.item.ask.type === 'tap') return L.blocks.map(b => ({ i: b.k, id: 'pv-s' + s.line + '_' + b.k, x: L.left + L.C0.w / 2 - 20, y: b.cy, w: L.C0.w - 60, h: b.h }));
    if (s.phase === 'ask') return L.askRows.map(r => ({ i: r.i, id: 'pv-ask' + s.line + '_' + r.i, x: r.x, y: r.y, w: r.w, h: r.h }));
    return [];
  };
  /* tests / screenshots: show this pool item as the current passage (a fresh instance, seeded) */
  PovScene.preview = (id, seed = 1) => {
    const s = Game.current(); if (!(s instanceof PovScene) || !s.items) return false;
    const it = json().items.find(x => x.id === id); if (!it) return false;
    s.items[s.line] = Narration.povInstance(it, Bank.rng(seed), () => 0);
    s.setup(s.line); s.lineAt = Game.time - 8; s.lays.clear();
    return true;
  };
  Levels.pov = PovScene;
})();
